import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { ArrowLeft, GitCompare, FileText, ShieldCheck, AlertTriangle, Camera, Pin } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, Select, TextArea, EmptyState } from "../../components/common/Primitives";
import api, { API_BASE } from "../../lib/api";

/* Reviewer note editor — pinned note per snapshot (mutable, audit-logged) */
function ReviewerNoteEditor({ snapshot, label, onSaved }) {
  const [note, setNote] = useState(snapshot.reviewer_note || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNote(snapshot.reviewer_note || ""); setEditing(false); }, [snapshot.snapshot_id]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/kyc-snapshots/detail/${snapshot.snapshot_id}/note`, { text: note });
      toast.success(data.unpinned ? "Note unpinned" : "Note pinned");
      setEditing(false);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    } finally { setSaving(false); }
  };
  const hasNote = !!(snapshot.reviewer_note || "").trim();
  return (
    <div className="mt-3 border border-navy/10 rounded p-3 bg-amber-50/40" data-testid={`snap-note-editor-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8A6600] flex items-center gap-1">
          <Pin size={11} /> Reviewer note · Snapshot {label}
        </div>
        {!editing && (
          <button
            className="text-[11px] font-mono uppercase tracking-widest text-[#8A6600] hover:underline"
            onClick={() => setEditing(true)}
            data-testid={`snap-note-edit-${label}`}
          >{hasNote ? "Edit" : "+ Pin note"}</button>
        )}
      </div>
      {editing ? (
        <>
          <TextArea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} testId={`snap-note-input-${label}`} />
          <div className="flex items-center gap-2 mt-2">
            <Button onClick={save} disabled={saving} testId={`snap-note-save-${label}`}>
              {saving ? "Saving…" : "Save note"}
            </Button>
            <Button variant="secondary" onClick={() => { setNote(snapshot.reviewer_note || ""); setEditing(false); }} testId={`snap-note-cancel-${label}`}>
              Cancel
            </Button>
            {hasNote && (
              <button
                className="text-xs text-rose-700 hover:underline ml-auto"
                onClick={() => { setNote(""); save(); }}
                data-testid={`snap-note-clear-${label}`}
              >Clear note</button>
            )}
          </div>
        </>
      ) : hasNote ? (
        <div data-testid={`snap-note-body-${label}`}>
          <div className="text-sm text-ink whitespace-pre-wrap leading-relaxed">{snapshot.reviewer_note}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-soft mt-2">
            — {snapshot.reviewer_note_by} · {snapshot.reviewer_note_at ? new Date(snapshot.reviewer_note_at).toLocaleString() : ""}
          </div>
        </div>
      ) : (
        <div className="text-xs text-ink-soft italic">No note pinned. Add a "why" so future reviewers see the reasoning without opening the audit log.</div>
      )}
    </div>
  );
}

/* Field-diff row */
function DiffRow({ label, a, b, testId }) {
  const same = JSON.stringify(a) === JSON.stringify(b);
  return (
    <tr className={same ? "" : "bg-rose-50/60"} data-testid={testId}>
      <td className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-ink-soft w-1/4">{label}</td>
      <td className={`px-3 py-2 text-sm ${same ? "text-ink-soft" : "text-rose-700 font-medium"} w-3/8 border-l border-navy/10`}>
        {a === null || a === undefined || a === "" ? <span className="italic text-ink-faint">(empty)</span> : String(a)}
      </td>
      <td className={`px-3 py-2 text-sm ${same ? "text-ink-soft" : "text-emerald-800 font-medium"} w-3/8 border-l border-navy/10`}>
        {b === null || b === undefined || b === "" ? <span className="italic text-ink-faint">(empty)</span> : String(b)}
      </td>
    </tr>
  );
}

function DocumentThumb({ doc, side }) {
  if (!doc) return <div className="h-32 flex items-center justify-center text-xs italic text-ink-faint">(not present)</div>;
  const url = doc.document_id ? `${API_BASE}/admin/documents/${doc.document_id}/view?disposition=inline` : null;
  const isPdf = (doc.content_type || "").includes("pdf") || (doc.filename || "").toLowerCase().endsWith(".pdf");
  return (
    <div className="space-y-1.5" data-testid={`snap-doc-${side}-${doc.document_id || doc.filename}`}>
      <div className="h-32 border border-navy/15 rounded overflow-hidden bg-white/70 flex items-center justify-center">
        {url && isPdf && doc.has_source_pdf ? (
          <iframe title={doc.filename} src={`${url}#toolbar=0&navpanes=0&scrollbar=0`} className="w-full h-full" />
        ) : (
          <FileText size={28} className="text-ink-faint" />
        )}
      </div>
      <div className="text-xs">
        <div className="font-mono truncate text-ink" title={doc.filename}>{doc.filename || "—"}</div>
        <div className="text-ink-soft">{doc.category || "—"} · {(doc.size_bytes || 0).toLocaleString()} B</div>
        <div className="text-[10px] font-mono text-ink-faint truncate" title={doc.checksum}>sig: {(doc.checksum || "").slice(0, 14)}…</div>
      </div>
    </div>
  );
}

export default function KycSnapshots() {
  const { entityType, entityId } = useParams();
  const [snapshots, setSnapshots] = useState([]);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const loadList = async () => {
    try {
      const { data } = await api.get(`/admin/kyc-snapshots/${entityType}/${entityId}`);
      const items = data.items || [];
      setSnapshots(items);
      if (items.length >= 2) {
        setAId(items[1].snapshot_id);   // second-newest as older
        setBId(items[0].snapshot_id);   // newest as newer
      } else if (items.length === 1) {
        setBId(items[0].snapshot_id);
      }
    } catch (e) { toast.error("Failed to load snapshots"); }
  };

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  const runCompare = async () => {
    if (!aId || !bId) { toast.error("Pick two snapshots"); return; }
    if (aId === bId) { toast.error("Pick two DIFFERENT snapshots"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/kyc-snapshots/compare/two?a=${encodeURIComponent(aId)}&b=${encodeURIComponent(bId)}`);
      setDiff(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compare failed");
    } finally { setLoading(false); }
  };

  const takeManualSnapshot = async () => {
    setCapturing(true);
    try {
      await api.post(`/admin/kyc-snapshots/${entityType}/${entityId}/manual`);
      toast.success("Snapshot captured");
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snapshot failed");
    } finally { setCapturing(false); }
  };

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to={entityType === "investor" ? "/admin/investors" : "/admin/kyc-queue"} className="text-xs text-ink-soft hover:text-gold flex items-center gap-1" data-testid="snap-back">
              <ArrowLeft size={12} /> Back
            </Link>
            <h1 className="font-display text-3xl text-ink mt-1 flex items-center gap-2">
              <GitCompare size={22} className="text-gold" /> KYC Snapshot Comparison
            </h1>
            <div className="text-xs text-ink-soft mt-1 font-mono uppercase tracking-widest">
              {entityType} · {entityId}
            </div>
          </div>
          <Button variant="secondary" onClick={takeManualSnapshot} disabled={capturing} testId="snap-capture-btn">
            <Camera size={14} className="inline mr-1" />{capturing ? "Capturing…" : "Capture snapshot now"}
          </Button>
        </div>

        <Card testId="snap-picker">
          <div className="overline mb-3">Select two snapshots</div>
          {snapshots.length < 2 ? (
            <EmptyState
              title="Need at least two snapshots"
              hint="Snapshots are auto-captured on every KYC status change. You can also capture one manually above."
            />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <Select label="Snapshot A (older)" value={aId} onChange={(e) => setAId(e.target.value)} testId="snap-select-a">
                {snapshots.map((s) => (
                  <option key={s.snapshot_id} value={s.snapshot_id}>
                    {s.reviewer_note ? "📌 " : ""}{new Date(s.created_at).toLocaleString()} · {s.kyc_status || "—"} · {s.reason}
                  </option>
                ))}
              </Select>
              <Select label="Snapshot B (newer)" value={bId} onChange={(e) => setBId(e.target.value)} testId="snap-select-b">
                {snapshots.map((s) => (
                  <option key={s.snapshot_id} value={s.snapshot_id}>
                    {s.reviewer_note ? "📌 " : ""}{new Date(s.created_at).toLocaleString()} · {s.kyc_status || "—"} · {s.reason}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="mt-4">
            <Button onClick={runCompare} disabled={loading || !aId || !bId} testId="snap-compare-btn">
              {loading ? "Comparing…" : "Compare selected"}
            </Button>
          </div>
        </Card>

        {diff && (
          <>
            <Card testId="snap-summary">
              <div className="overline mb-2">Summary</div>
              <div className="flex flex-wrap gap-2">
                <Pill tone={diff.diff.summary.field_changes ? "warn" : "muted"} testId="snap-sum-fields">
                  {diff.diff.summary.field_changes} field changes
                </Pill>
                <Pill tone={diff.diff.summary.docs_added ? "success" : "muted"} testId="snap-sum-added">
                  {diff.diff.summary.docs_added} docs added
                </Pill>
                <Pill tone={diff.diff.summary.docs_removed ? "danger" : "muted"} testId="snap-sum-removed">
                  {diff.diff.summary.docs_removed} docs removed
                </Pill>
                <Pill tone={diff.diff.summary.docs_changed ? "warn" : "muted"} testId="snap-sum-changed">
                  {diff.diff.summary.docs_changed} docs changed
                </Pill>
              </div>
              {(diff.diff.summary.docs_changed > 0 || diff.diff.summary.docs_removed > 0) && (
                <div className="mt-3 text-xs text-rose-700 flex items-center gap-1.5" data-testid="snap-tamper-warn">
                  <AlertTriangle size={12} /> Docs changed or removed between snapshots — verify checksum drift below.
                </div>
              )}
              {/* Reviewer notes — one per snapshot, mutable, audit-logged */}
              <div className="grid md:grid-cols-2 gap-3">
                <ReviewerNoteEditor
                  snapshot={diff.a}
                  label="A"
                  onSaved={(updated) => {
                    setDiff((d) => ({ ...d, a: { ...d.a, ...updated, reviewer_note: updated.unpinned ? "" : updated.reviewer_note } }));
                    loadList();
                  }}
                />
                <ReviewerNoteEditor
                  snapshot={diff.b}
                  label="B"
                  onSaved={(updated) => {
                    setDiff((d) => ({ ...d, b: { ...d.b, ...updated, reviewer_note: updated.unpinned ? "" : updated.reviewer_note } }));
                    loadList();
                  }}
                />
              </div>
            </Card>

            {/* Verified fields diff */}
            <Card testId="snap-fields-card">
              <div className="overline mb-3">KYC State</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-mono uppercase tracking-widest text-ink-soft border-b border-navy/10">
                    <th className="text-left px-3 py-2">Field</th>
                    <th className="text-left px-3 py-2 border-l border-navy/10">Snapshot A</th>
                    <th className="text-left px-3 py-2 border-l border-navy/10">Snapshot B</th>
                  </tr>
                </thead>
                <tbody>
                  {["kyc_status","admin_temp_approved","admin_approved_by","admin_approved_at","kyc_note"].map((k) => (
                    <DiffRow key={k} label={k} a={diff.a[k]} b={diff.b[k]} testId={`snap-diff-${k}`} />
                  ))}
                  {Array.from(new Set([...(Object.keys(diff.a.verified_fields||{})), ...(Object.keys(diff.b.verified_fields||{}))])).map((k) => (
                    <DiffRow key={k} label={`verified.${k}`} a={(diff.a.verified_fields||{})[k]} b={(diff.b.verified_fields||{})[k]} testId={`snap-diff-vf-${k}`} />
                  ))}
                </tbody>
              </table>
            </Card>

            {/* Documents diff — side by side thumbnails */}
            <Card testId="snap-docs-card">
              <div className="overline mb-3">Documents</div>

              {diff.diff.documents.changed.length > 0 && (
                <div className="mb-6" data-testid="snap-docs-changed">
                  <div className="text-xs font-mono uppercase tracking-widest text-amber-700 mb-2">
                    Changed ({diff.diff.documents.changed.length}) — checksum or metadata drift
                  </div>
                  <div className="space-y-4">
                    {diff.diff.documents.changed.map((c) => (
                      <div key={c.document_id || c.filename} className="border border-amber-200 bg-amber-50/40 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-ink">{c.filename || c.document_id}</div>
                          <Pill tone="warn">{Object.keys(c.diffs).join(", ")}</Pill>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <DocumentThumb doc={c.a} side="a" />
                          <DocumentThumb doc={c.b} side="b" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {diff.diff.documents.added.length > 0 && (
                <div className="mb-6" data-testid="snap-docs-added">
                  <div className="text-xs font-mono uppercase tracking-widest text-emerald-700 mb-2">
                    Added ({diff.diff.documents.added.length})
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    {diff.diff.documents.added.map((d) => (
                      <DocumentThumb key={d.document_id || d.filename} doc={d} side="b" />
                    ))}
                  </div>
                </div>
              )}

              {diff.diff.documents.removed.length > 0 && (
                <div className="mb-2" data-testid="snap-docs-removed">
                  <div className="text-xs font-mono uppercase tracking-widest text-rose-700 mb-2">
                    Removed ({diff.diff.documents.removed.length})
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    {diff.diff.documents.removed.map((d) => (
                      <DocumentThumb key={d.document_id || d.filename} doc={d} side="a" />
                    ))}
                  </div>
                </div>
              )}

              {diff.diff.documents.added.length === 0 &&
                diff.diff.documents.removed.length === 0 &&
                diff.diff.documents.changed.length === 0 && (
                <div className="text-sm text-emerald-800 flex items-center gap-1.5" data-testid="snap-docs-noop">
                  <ShieldCheck size={13} /> No document differences between these two snapshots.
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
